import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface SettingRow {
  key: string;
  value: any;
  category: string;
  description: string;
}

export function PlatformSettings() {
  const { data: adminInfo } = usePlatformAdmin();
  
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [originalSettings, setOriginalSettings] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Permission check
  if (!adminInfo?.permissions?.can_manage_platform) {
    return (
      <Card>
        <CardContent className="py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You do not have permission to manage platform settings.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Fetch settings
  const { data: settingsData, isLoading, error, refetch } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true });
      
      if (error) throw error;
      return data as SettingRow[];
    }
  });

  // Transform settings data
  useEffect(() => {
    if (settingsData) {
      const settingsObj = settingsData.reduce((acc, setting) => {
        let parsedValue = setting.value;
        
        // Handle string values (remove quotes from JSONB)
        if (typeof parsedValue === 'string' && parsedValue.startsWith('"') && parsedValue.endsWith('"')) {
          parsedValue = parsedValue.slice(1, -1);
        }
        
        acc[setting.key] = parsedValue;
        return acc;
      }, {} as Record<string, any>);
      
      setSettings(settingsObj);
      setOriginalSettings(settingsObj);
    }
  }, [settingsData]);

  // Check for changes
  useEffect(() => {
    const changed = Object.keys(settings).some(
      key => JSON.stringify(settings[key]) !== JSON.stringify(originalSettings[key])
    );
    setHasChanges(changed);
  }, [settings, originalSettings]);

  // Number field validation rules
  const getValidationRules = (key: string): { min: number; max: number; label: string } | null => {
    const rules: Record<string, { min: number; max: number; label: string }> = {
      'security.session_timeout_minutes': { min: 1, max: 1440, label: 'Session timeout' },
      'proxy.max_duration_admin_manager': { min: 1, max: 480, label: 'Admin Manager duration' },
      'proxy.max_duration_admin_consultant': { min: 1, max: 240, label: 'Admin Consultant duration' },
      'audit.retention_days': { min: 1, max: 3650, label: 'Retention period' }
    };
    return rules[key] || null;
  };

  // Validate number field
  const validateNumberField = (key: string, value: string): string | null => {
    const rules = getValidationRules(key);
    if (!rules) return null;

    const numValue = parseFloat(value);
    
    if (isNaN(numValue)) {
      return `${rules.label} must be a valid number`;
    }
    
    if (!Number.isInteger(numValue)) {
      return `${rules.label} must be a whole number (no decimals)`;
    }
    
    if (numValue < rules.min) {
      return `${rules.label} must be at least ${rules.min}`;
    }
    
    if (numValue > rules.max) {
      return `${rules.label} cannot exceed ${rules.max}`;
    }
    
    return null;
  };

  // Handle setting change
  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle number field change with validation
  const handleNumberChange = (key: string, value: string) => {
    const error = validateNumberField(key, value);
    
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[key] = error;
      } else {
        delete newErrors[key];
      }
      return newErrors;
    });
    
    if (!error) {
      handleSettingChange(key, parseInt(value));
    }
  };

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      // Pre-save validation
      const validationErrors: Record<string, string> = {};
      
      Object.keys(settings).forEach(key => {
        const rules = getValidationRules(key);
        if (rules) {
          const error = validateNumberField(key, String(settings[key]));
          if (error) {
            validationErrors[key] = error;
          }
        }
      });
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        throw new Error('Please fix validation errors before saving');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const changedKeys = Object.keys(settings).filter(
        key => JSON.stringify(settings[key]) !== JSON.stringify(originalSettings[key])
      );
      
      for (const key of changedKeys) {
        let valueToStore = settings[key];
        
        // Wrap strings in quotes for JSONB
        if (typeof valueToStore === 'string') {
          valueToStore = `"${valueToStore}"`;
        } else {
          valueToStore = String(valueToStore);
        }
        
        const { error } = await supabase
          .from('platform_settings')
          .update({
            value: valueToStore,
            updated_at: new Date().toISOString(),
            updated_by: user.id
          })
          .eq('key', key);
        
        if (error) throw error;
      }

      const { error: logError } = await supabase.rpc('log_platform_admin_action', {
        p_action_type: 'settings_update',
        p_target_type: 'platform',
        p_target_id: null,
        p_organization_id: null,
        p_justification: 'Platform settings updated',
        p_metadata: {
          updated_settings: changedKeys,
          changes: changedKeys.reduce((acc, key) => {
            acc[key] = {
              from: originalSettings[key],
              to: settings[key]
            };
            return acc;
          }, {} as Record<string, any>)
        }
      });

      if (logError) {
        // Failed to log action - non-critical
      }
    },
    onSuccess: () => {
      toast.success('Settings saved successfully');
      setShowConfirmDialog(false);
      refetch();
    },
    onError: (error: any) => {
      toast.error(`Failed to save settings: ${error.message}`);
    }
  });

  const renderSettingField = (key: string, label: string, description: string, type: string, options?: string[]) => {
    const value = settings[key];

    if (type === 'boolean') {
      return (
        <div className="flex items-center justify-between space-x-2 py-4 border-b">
          <div className="space-y-0.5">
            <Label htmlFor={key}>{label}</Label>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <Switch
            id={key}
            checked={value === 'true' || value === true}
            onCheckedChange={(checked) => handleSettingChange(key, checked)}
          />
        </div>
      );
    }

    if (type === 'number') {
      const rules = getValidationRules(key);
      const hasError = !!errors[key];
      
      return (
        <div className="space-y-2 py-4 border-b">
          <Label htmlFor={key}>{label}</Label>
          <p className="text-sm text-muted-foreground mb-2">{description}</p>
          <Input
            id={key}
            type="number"
            min={rules?.min}
            max={rules?.max}
            step={1}
            value={value || ''}
            onChange={(e) => handleNumberChange(key, e.target.value)}
            className={hasError ? "border-destructive" : ""}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${key}-error` : undefined}
          />
          {hasError && (
            <p id={`${key}-error`} className="text-sm text-destructive">
              {errors[key]}
            </p>
          )}
        </div>
      );
    }

    if (type === 'select' && options) {
      return (
        <div className="space-y-2 py-4 border-b">
          <Label htmlFor={key}>{label}</Label>
          <p className="text-sm text-muted-foreground mb-2">{description}</p>
          <Select value={value || ''} onValueChange={(val) => handleSettingChange(key, val)}>
            <SelectTrigger id={key}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (type === 'text' || type === 'textarea') {
      return (
        <div className="space-y-2 py-4 border-b">
          <Label htmlFor={key}>{label}</Label>
          <p className="text-sm text-muted-foreground mb-2">{description}</p>
          {type === 'textarea' ? (
            <Textarea
              id={key}
              value={value || ''}
              onChange={(e) => handleSettingChange(key, e.target.value)}
              rows={3}
            />
          ) : (
            <Input
              id={key}
              value={value || ''}
              onChange={(e) => handleSettingChange(key, e.target.value)}
            />
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Platform Settings</CardTitle>
            <CardDescription>
              Configure platform-wide settings and features
            </CardDescription>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Unsaved Changes</Badge>
              <Button 
                onClick={() => setShowConfirmDialog(true)}
                disabled={Object.keys(errors).length > 0}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="text-center py-8 text-muted-foreground">
            Loading settings...
          </div>
        )}
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load settings: {(error as Error).message}
            </AlertDescription>
          </Alert>
        )}
        
        {!isLoading && !error && (
          <Tabs defaultValue="security" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="proxy">Proxy</TabsTrigger>
              <TabsTrigger value="audit">Audit</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
            </TabsList>
            
            <TabsContent value="security" className="space-y-4 mt-4">
              {renderSettingField('security.mfa_required', 'Require MFA', 'Require multi-factor authentication for all platform admins', 'boolean')}
              {renderSettingField('security.session_timeout_minutes', 'Session Timeout (minutes)', 'Minutes of inactivity before automatic logout', 'number')}
              {renderSettingField('security.password_complexity', 'Password Complexity', 'Minimum password requirements', 'select', ['minimal', 'standard', 'high'])}
              {renderSettingField('security.ip_allowlist', 'IP Allowlist', 'Comma-separated list of allowed IP ranges (optional)', 'textarea')}
            </TabsContent>
            
            <TabsContent value="proxy" className="space-y-4 mt-4">
              {renderSettingField('proxy.justification_required', 'Require Justification', 'Require justification for all proxy sessions', 'boolean')}
              {renderSettingField('proxy.max_duration_admin_manager', 'Max Duration - Admin Manager (minutes)', 'Maximum proxy session duration for admin managers', 'number')}
              {renderSettingField('proxy.max_duration_admin_consultant', 'Max Duration - Admin Consultant (minutes)', 'Maximum proxy session duration for admin consultants', 'number')}
              {renderSettingField('proxy.auto_end_inactive', 'Auto-end Inactive Sessions', 'Automatically end proxy sessions after inactivity', 'boolean')}
            </TabsContent>
            
            <TabsContent value="audit" className="space-y-4 mt-4">
              {renderSettingField('audit.retention_days', 'Log Retention (days)', 'Number of days to retain audit logs', 'number')}
              {renderSettingField('audit.log_detail_level', 'Log Detail Level', 'Level of detail captured in audit logs', 'select', ['minimal', 'standard', 'verbose'])}
              {renderSettingField('audit.export_enabled', 'Enable Export', 'Allow admins to export audit logs', 'boolean')}
            </TabsContent>
            
            <TabsContent value="features" className="space-y-4 mt-4">
              {renderSettingField('features.advanced_analytics', 'Advanced Analytics', 'Enable advanced analytics features', 'boolean')}
              {renderSettingField('features.bulk_operations', 'Bulk Operations', 'Enable bulk user and organization operations', 'boolean')}
              {renderSettingField('features.api_access', 'API Access', 'Enable API access for integrations', 'boolean')}
            </TabsContent>
          </Tabs>
        )}
        
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save Settings?</AlertDialogTitle>
              <AlertDialogDescription>
                This will update platform-wide settings. All admins will be affected by these changes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => saveSettingsMutation.mutate()}
                disabled={saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
